/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { Text, ActionButton } from '@components/atoms'
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	ButtonPropsColorOverrides,
} from '@mui/material'
import { OverridableStringUnion } from '@mui/types'

import { grey, pink, blue } from '@mui/material/colors'

export interface ModalInfoType {
	target: string
	title: string
	content?: JSX.Element
	value?: string
	onSubmit?: (value?: any) => void
}

interface PropsType {
	show: boolean

	target: string
	title: string
	customTitle?: JSX.Element
	showTitle?: boolean

	value?: string

	// close
	closeTitle?: string
	closeStatus?: OverridableStringUnion<
		'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning',
		ButtonPropsColorOverrides
	>
	closeIcon?: string
	closeIconColor?: string
	showClose?: boolean

	// submit
	submitTitle?: string
	submitStatus?: OverridableStringUnion<
		'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning',
		ButtonPropsColorOverrides
	>
	isSubmitDisabled?: boolean
	submitIcon?: string
	submitIconColor?: string
	showSubmit?: boolean
	isDisableDisimissionOnSubmit?: boolean

	// event
	onSubmit?: (value?: any) => void
	onCloseModal?: () => void

	// style
	isTitleDanger?: boolean
	style?: React.CSSProperties[] | React.CSSProperties
	rootStyle?: React.CSSProperties

	// contents
	children?: React.ReactNode
}

export type { PropsType as ModalTypes }

export const Modal = (props: PropsType) => {
	// state
	const [showModal, setShowModal] = React.useState<boolean>(props.show)

	// variables
	const showTitle: boolean = props.showTitle ?? true
	const isTitleDanger: boolean = props.isTitleDanger ?? false
	const closeStatus: OverridableStringUnion<
		'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning',
		ButtonPropsColorOverrides
	> = props.closeStatus ?? 'primary'
	const submitStatus: OverridableStringUnion<
		'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning',
		ButtonPropsColorOverrides
	> = props.submitStatus ?? 'primary'
	const showClose: boolean = props.showClose ?? true
	const showSubmit: boolean = props.showSubmit ?? true
	const isDisableDisimissionOnSubmit: boolean =
		props.isDisableDisimissionOnSubmit ?? false

	React.useEffect(() => {
		setShowModal(props.show)
	}, [props.show])

	const onCloseModal = () => {
		setShowModal(false)
		if (props.onCloseModal !== undefined) {
			props.onCloseModal()
		}
	}

	const onSubmit = () => {
		if (!isDisableDisimissionOnSubmit) {
			setShowModal(false)
		}

		if (!isDisableDisimissionOnSubmit && props.onCloseModal !== undefined) {
			props.onCloseModal()
		}
		if (props.onSubmit !== undefined) {
			props.onSubmit()
		}
	}

	return (
		<Dialog
			open={showModal}
			onClose={onCloseModal}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			css={[
				style,
				props.rootStyle !== undefined
					? { '.MuiPaper-elevation': { ...props.rootStyle } }
					: {},
			]}
		>
			{showTitle ? (
				<DialogTitle
					id="alert-dialog-title"
					className="modalTitle"
					css={isTitleDanger ? { color: pink['A400'] } : null}
				>
					<Text className="dialogTitleText">{props.title}</Text>
				</DialogTitle>
			) : null}

			<DialogContent sx={{ ...props.style }} className="modalContentView">
				{props.children}
			</DialogContent>

			<DialogActions className="modalActionView">
				{showClose ? (
					<ActionButton
						title={props.closeTitle ?? '닫기'}
						tooltip={props.closeTitle ?? '닫기'}
						buttonColor={closeStatus}
						onClick={onCloseModal}
						iconName={props.closeIcon ?? 'close-thick'}
						iconTextColor={props.closeIconColor ?? grey[700]}
					/>
				) : null}

				{showSubmit && props.target !== 'duplicatedNameWithMovingAlert' ? (
					<ActionButton
						title={props.submitTitle ?? '확인'}
						tooltip={props.submitTitle ?? '확인'}
						buttonColor={submitStatus}
						onClick={onSubmit}
						iconName={props.submitIcon ?? 'check-bold'}
						iconTextColor={props.submitIconColor ?? blue['A400']}
						disabled={props.isSubmitDisabled === false}
					/>
				) : null}
			</DialogActions>
		</Dialog>
	)
}

const style = css({
	minWidth: '30rem',
	minHeight: '8rem',

	'.modalTitle': {
		display: 'flex',
		alignItems: 'center',
		borderBottom: `1px solid ${grey[100]}`,

		div: {
			'.dialogTitleText': {
				margin: 0,
			},
		},
	},

	'.modalContentView': {
		minWidth: 300,
		overflowX: 'hidden',
	},

	'.modalActionView': {
		borderTop: `1px solid ${grey[100]}`,
	},

	'@media (max-width: 500px)': {
		minWidth: '100%',
		width: '100%',

		'.MuiPaper-elevation': {
			width: '100%',
			margin: 0,
		},

		'.modalContentView': {
			minWidth: '100%',
		},
	},
})
